import * as React from 'react';

interface UseVideoProgressProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  progressBarRef: React.RefObject<HTMLButtonElement | null>;
  duration: number;
  isPlaying: boolean;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
}

export const useVideoProgress = ({
  videoRef,
  progressBarRef,
  duration,
  isPlaying,
  onTimeUpdate,
}: UseVideoProgressProps) => {
  const [currentTime, setCurrentTime] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const current = videoRef.current.currentTime;
    const total = videoRef.current.duration;
    // Only update for the callback, not for UI rendering
    onTimeUpdate?.(current, total);
  };

  /**
   * This effect is to update progress bar smoothly @60fps
   * This is done by using requestAnimationFrame.
   */
  React.useEffect(() => {
    if (!isPlaying || isDragging) return () => {};

    let animationFrameId: number;

    const updateProgressBar = () => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
      }
      animationFrameId = requestAnimationFrame(updateProgressBar);
    };

    animationFrameId = requestAnimationFrame(updateProgressBar);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isPlaying, isDragging, videoRef]);

  const updateProgress = React.useCallback(
    (clientX: number) => {
      if (!progressBarRef.current || !videoRef.current) return;
      const rect = progressBarRef.current.getBoundingClientRect();
      const percent = Math.max(
        0,
        Math.min(1, (clientX - rect.left) / rect.width)
      );
      const newTime = percent * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [duration, progressBarRef, videoRef]
  );

  const handleProgressClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isDragging) {
      updateProgress(e.clientX);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDragging(true);
    updateProgress(e.clientX);
  };

  // Use effect to add/remove global listeners only when dragging
  React.useEffect(() => {
    if (!isDragging) return () => {};

    const handleGlobalMouseMove = (e: MouseEvent) => {
      updateProgress(e.clientX);
    };

    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    // Add listeners when dragging starts
    globalThis.addEventListener('mousemove', handleGlobalMouseMove);
    globalThis.addEventListener('mouseup', handleGlobalMouseUp);

    // Cleanup when dragging stops or component unmounts
    return () => {
      globalThis.removeEventListener('mousemove', handleGlobalMouseMove);
      globalThis.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, updateProgress]);

  return {
    currentTime,
    isDragging,
    handleTimeUpdate,
    handleProgressClick,
    handleMouseDown,
  };
};
